import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import type {
  ResponseByPath,
  QueryParamsByPath,
  SchemaAttributes,
} from '@/api/types.js'
import { useApi } from '@/composables/useApi.js'

const LIST_ITEMS_LIST_URL = '/{provet_id}/api/list_items'
export type ListItemsQueryParams = QueryParamsByPath<
  'get',
  typeof LIST_ITEMS_LIST_URL
>

export type ListItemsListResponse = ResponseByPath<
  'get',
  typeof LIST_ITEMS_LIST_URL
>

export const ListItemsTypes = {
  Species: 'SPECIES',
  Breed: 'BREED',
  Diagnosis: 'DIAGNOSIS',
  Insurance: 'INSURANCE',
  ConsultationNoteType: 'CONSULTATION_NOTE_TYPE',
  ReportingDimension1: 'REPORTING_DIMENSION_1',
  ReportingDimension2: 'REPORTING_DIMENSION_2',
  PatientStatus1: 'PATIENT_STATUS_1',
  PatientStatus2: 'PATIENT_STATUS_2',
  PatientStatus3: 'PATIENT_STATUS_3',
  PatientStatus4: 'PATIENT_STATUS_4',
  PatientStatus5: 'PATIENT_STATUS_5',
  PatientStatus6: 'PATIENT_STATUS_6',
  PatientStatus7: 'PATIENT_STATUS_7',
  PatientStatus8: 'PATIENT_STATUS_8',
  PatientStatus9: 'PATIENT_STATUS_9',
  PatientStatus10: 'PATIENT_STATUS_10',
  PatientStatus11: 'PATIENT_STATUS_11',
  PatientStatus12: 'PATIENT_STATUS_12',
  Statement: 'STATEMENT',
  ComplicationCategory: 'COMPLICATION_CAT',
  ComplicationSubcategory: 'COMPLICATION_SUBCAT',
  LaboratoryReport1: 'LABORATORY_REPORT_1',
  LaboratoryReport2: 'LABORATORY_REPORT_2',
  LaboratoryReport3: 'LABORATORY_REPORT_3',
  SampleType: 'SAMPLE_TYPE',
  VetEnvoyInsurance: 'VETENVOY_INSURANCE',
  ReasonType: 'REASON_TYPE',
  MedicineDiagnosisType: 'MEDICINE_DIAGNOSIS_TYPE',
  ReferralSendingReason: 'REFERRAL_SENDING_REASON',
  ReferralAreaOfExpertise: 'REFERRAL_AREA_OF_EXPERTISE',
  ItemEntrySubtype: 'ITEM_ENTRY_SUBTYPE',
  ClinicalSign: 'CLINICAL_SIGN',
} as const

export type ListItem = (typeof ListItemsTypes)[keyof typeof ListItemsTypes]

type ListItemAttributes = SchemaAttributes<'ListItem'>
// Aliases
export type InsuranceCompany = ListItemAttributes

const listItemKeys = {
  all: ['listItems'] as const,
  lists: () => [...listItemKeys.all, 'list'] as const,
  list: (query: MaybeRef<ListItemsQueryParams>) =>
    [...listItemKeys.lists(), query] as const,
  detail: (id: MaybeRef<string>) =>
    [...listItemKeys.all, 'detail', id] as const,
}

export function useListItemListQuery({
  filters,
  enabled = true,
}: {
  filters: {
    listType: MaybeRef<ListItem>
    departmentIds?: MaybeRef<string[]>
    search?: MaybeRef<string>
  }
  enabled?: MaybeRef<boolean>
}) {
  const api = useApi()
  const route = useRoute()

  const queryParams = computed<ListItemsQueryParams>(
    () =>
      ({
        'filter[list_type]': unref(filters.listType),
        'filter[department_ids]': unref(filters.departmentIds),
        'filter[search]': unref(filters.search),
      }) satisfies ListItemsQueryParams,
  )

  const query = useQuery({
    placeholderData: keepPreviousData,
    enabled,
    queryKey: listItemKeys.list(queryParams),
    queryFn: async () =>
      await api((client) =>
        client.GET(LIST_ITEMS_LIST_URL, {
          params: {
            path: { provet_id: route.params.provetId },
            query: unref(queryParams),
          },
        }),
      ),
  })

  return reactive(query)
}
